import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, PlusCircle, FileText, Package, UserCircle,
  LogOut, ArrowLeft,
} from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/painel", label: "Dashboard", icon: LayoutDashboard, testId: "portal-nav-dashboard", end: true },
  { to: "/painel/orcamentos/novo", label: "Novo Orçamento", icon: PlusCircle, testId: "portal-nav-new-quote" },
  { to: "/painel/orcamentos", label: "Meus Orçamentos", icon: FileText, testId: "portal-nav-quotes", end: true },
  { to: "/painel/pedidos", label: "Pedidos", icon: Package, testId: "portal-nav-orders" },
  { to: "/painel/perfil", label: "Perfil", icon: UserCircle, testId: "portal-nav-profile" },
];

export default function ClientLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-zinc-950 text-zinc-100" data-testid="client-portal">
      <aside className="hidden md:flex flex-col w-64 border-r border-zinc-800 bg-zinc-950">
        <div className="px-5 py-5 border-b border-zinc-800">
          <Logo />
        </div>

        <nav className="mt-4 px-3 flex-1 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                  isActive
                    ? "bg-orange-500/10 text-orange-400 border-l-2 border-orange-500"
                    : "text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 border-l-2 border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={1.75} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-zinc-800 space-y-2">
          <Link
            to="/"
            data-testid="portal-back-to-site-link"
            className="flex items-center gap-2 px-3 py-2 rounded-sm text-xs text-zinc-500 hover:text-orange-400 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao site
          </Link>
          <div className="text-xs text-zinc-500 truncate px-3" data-testid="portal-user-email">
            {user?.email}
          </div>
          <button
            data-testid="portal-logout-button"
            onClick={doLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-zinc-400 hover:bg-zinc-900 hover:text-zinc-100 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </aside>

      <div className="md:hidden fixed top-0 inset-x-0 z-30 flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
        <Link to="/painel"><Logo compact /></Link>
        <button onClick={doLogout} className="text-xs text-zinc-400" data-testid="portal-logout-button-mobile">
          Sair
        </button>
      </div>

      <main className="flex-1 pt-[60px] md:pt-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 md:pb-10">
          {children}
        </div>
      </main>

      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 grid grid-cols-5 text-[10px]">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={`${n.testId}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center py-2 gap-1 ${isActive ? "text-orange-400" : "text-zinc-500"}`
            }
          >
            <n.icon className="w-4 h-4" strokeWidth={1.75} />
            {n.label.split(" ")[0] === "Novo" ? "Novo" : n.label.split(" ").slice(-1)[0]}
          </NavLink>
        ))}
      </div>
    </div>
  );
}
